import { type ReactNode, useMemo } from "react";
import { Button } from "../../Controls/Button";
import type { DialogResult } from "../DialogResult";

/**
 * Builds the default Cancel/OK footer when a Dialog has onOk or onCancel but
 * no custom `footer` override. Shared between DialogInline_ (Storybook) and
 * DialogInner_ (modal) so they can't drift on button labels, loading state,
 * or click handlers.
 */
export function useDefaultDialogFooter({
  footer,
  onOk,
  onCancel,
  okLoading,
  okText,
  cancelText,
  onOkClick,
  onCancelClick,
  okDisabled = false,
  okDisabledMessage = "No changes to save",
}: {
  footer?: ReactNode;
  onOk?: (() => void | DialogResult | Promise<void | DialogResult>) | undefined;
  onCancel?: (() => void) | undefined;
  okLoading: boolean;
  okText: string;
  cancelText: string;
  onOkClick: () => void | Promise<void>;
  onCancelClick: () => void;
  okDisabled?: boolean;
  okDisabledMessage?: string;
}): ReactNode {
  return useMemo(() => {
    if (footer !== undefined) return footer;
    if (!onOk && !onCancel) return undefined;
    return (
      <>
        <Button
          data-testid="dialog-cancel"
          onClick={onCancelClick}
          disabled={okLoading}
          disabledMessage="Loading..."
        >
          {cancelText}
        </Button>
        {okDisabled && !okLoading ? (
          <Button
            data-testid="dialog-ok"
            primary
            onClick={onOkClick}
            disabled
            disabledMessage={okDisabledMessage}
          >
            {okText}
          </Button>
        ) : (
          <Button
            data-testid="dialog-ok"
            primary
            onClick={onOkClick}
            loading={okLoading}
          >
            {okText}
          </Button>
        )}
      </>
    );
  }, [
    footer,
    onOk,
    onCancel,
    okLoading,
    okText,
    cancelText,
    onOkClick,
    onCancelClick,
    okDisabled,
  ]);
}
